// dev/app-skeleton/src/lib/entries.ts
// 写一条 entry：明文→entry_key→AEAD→入 ops 队列 + 入本地 entries 表。
// D14-DEV-v2 / SEC C' 修：不再把明文写本地 IDB；写入后直接 prime 内存 LRU。
// D15 / Task 1 修：写完立刻 kickSync()，让 /api/sync/push 真发出。
import { ulid } from "ulid";
import { aeadOpen, aeadSeal, buildAAD, zeroize } from "../crypto";
import { db } from "./db";
import { deriveFromRoot } from "./session-key";
import { primeEntryCache, evictEntryCache } from "./entry-cache";
import { kickSync } from "./sync-client";
export async function upsertEntry(userId, entryId, pt) {
    const existing = await db.entries.get(entryId);
    const nextVersion = (existing?.local_known_server_version ?? 0) + 1; // 仅作 AAD 用，server 会确认/分配
    const key = deriveFromRoot("entry", entryId);
    try {
        const aad = buildAAD({ kind: "entry", user_id: userId, resource_id: entryId, version: nextVersion });
        const { bytes } = aeadSeal(key, new TextEncoder().encode(JSON.stringify(pt)), aad);
        const now = Date.now();
        const row = {
            id: entryId,
            encrypted_blob: bytes,
            // D14-DEV-v2 / SEC C' 修：不再把明文写本地 IDB。读路径走 entry-cache.ts。
            local_known_server_version: existing?.local_known_server_version ?? null,
            dirty: true,
            deleted: false,
            created_at: existing?.created_at ?? now,
            updated_at: now,
        };
        await db.transaction("rw", db.entries, db.ops, async () => {
            await db.entries.put(row);
            await db.ops.put({
                op_local_id: ulid(),
                op: "upsert_entry",
                entity_kind: "entry",
                entity_id: entryId,
                base_version: existing?.local_known_server_version ?? null,
                encrypted_blob: bytes,
                client_ts: now,
                status: "pending",
                attempts: 0,
            });
        });
        // 写入成功后立刻 prime 缓存：read 路径 (decryptEntryCached) 直接命中无需再解。
        primeEntryCache(userId, entryId, pt, nextVersion);
    }
    finally {
        zeroize(key);
    }
    // D15 Task 1：保存后立刻触发同步推送。kickSync 单飞 + 静默吞错，不阻塞 UI。
    void kickSync();
}
export async function decryptEntry(userId, entryId, blob, version) {
    const key = deriveFromRoot("entry", entryId);
    try {
        const aad = buildAAD({ kind: "entry", user_id: userId, resource_id: entryId, version });
        const pt = aeadOpen(key, blob, aad);
        return JSON.parse(new TextDecoder().decode(pt));
    }
    finally {
        zeroize(key);
    }
}
export function newEntryId() {
    return crypto.randomUUID();
}
/** 软删除：留墓碑（deleted=true + dirty=true），等 sync 推到服务端再决定是否物理清。
 *  + 落 ops 队列：op="delete_entry"，base_version = local_known_server_version。
 *  + D14-DEV-v2：evict 内存缓存，避免软删后 UI 还能看到明文。
 *  + D15 Task 1：删完同样 kickSync()。 */
export async function deleteEntry(userId, entryId) {
    const existing = await db.entries.get(entryId);
    if (!existing)
        return;
    const now = Date.now();
    await db.transaction("rw", db.entries, db.ops, async () => {
        await db.entries.put({
            ...existing,
            deleted: true,
            dirty: true,
            updated_at: now,
        });
        await db.ops.put({
            op_local_id: ulid(),
            op: "delete_entry",
            entity_kind: "entry",
            entity_id: entryId,
            base_version: existing.local_known_server_version,
            client_ts: now,
            status: "pending",
            attempts: 0,
        });
    });
    evictEntryCache(userId, entryId);
    void kickSync();
}
